import { Box, Button } from '@mui/material'
import DropdownCaretIcon from '@mui/icons-material/ArrowDropDown'
import VenueIcon from '@mui/icons-material/StorefrontSharp'

type VenueTriggerButtonProps = {
  readonly label: string
  /** Compact label shown on mobile in place of `label`; defaults to `label`. */
  readonly shortLabel?: string
  readonly disabled?: boolean
  readonly isOpen: boolean
  readonly onClick: (event: React.MouseEvent<HTMLElement>) => void
}

/**
 * The topbar trigger button shared by both switcher modes. The hover treatment
 * matches the other topbar buttons (see `AppMenuFlyout`), which sit on the
 * brand-coloured header rather than a palette surface.
 *
 * Two presentations across the smxl (900px) boundary, both keeping the dropdown
 * caret so the control always reads as changeable:
 *  - desktop: an icon then the full venue/selection label and the caret.
 *  - mobile: the icon is dropped and the compact `shortLabel` is shown instead,
 *    so the trigger stays narrow in the cramped right region. The label keeps
 *    the default button font size for legibility and ellipsizes under a
 *    viewport cap so a long name can't overflow the topbar.
 */
const VenueTriggerButton = ({
  label,
  shortLabel,
  disabled = false,
  isOpen,
  onClick,
}: VenueTriggerButtonProps) => (
  <Button
    onClick={onClick}
    disabled={disabled}
    aria-haspopup="true"
    aria-expanded={isOpen ? 'true' : undefined}
    aria-label={label}
    sx={{
      textTransform: 'none',
      minWidth: 0,
      // Tighter horizontal padding on mobile to save space, but a 44px min
      // height so the trigger keeps an accessible touch target there (a medium
      // Button is otherwise ~36px tall).
      paddingX: 0,
      minHeight: { xs: 44, smxl: 'auto' },
      color: (theme) => theme.app.headerTextColor,
      '&.Mui-disabled': {
        color: (theme) => theme.app.headerTextColor,
        opacity: 0.7,
      },
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    }}
  >
    <VenueIcon sx={{ display: { xs: 'none', smxl: 'inline-flex' } }} />
    <Box
      component="span"
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
      // The compact short name on mobile, the full label from smxl up; the
      // mobile cap guards against a long name overflowing the right region.
      maxWidth={{ xs: '32vw', smxl: 'none' }}
      sx={{
        paddingLeft: 1,
        paddingRight: 1,
      }}
    >
      <Box
        component="span"
        display={{
          xs: 'none',
          smxl: 'inline',
        }}
        sx={{
          fontWeight: 100,
          fontSize: '13px',
        }}
      >
        {label}
      </Box>
      <Box
        component="span"
        display={{
          xs: 'inline',
          smxl: 'none',
        }}
        sx={{
          fontWeight: 100,
          fontSize: '13px',
        }}
      >
        {shortLabel ?? label}
      </Box>
    </Box>
    <DropdownCaretIcon />
  </Button>
)

export default VenueTriggerButton
